import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/App';

/**
 * AuthCallback — Processes Google OAuth tokens from URL fragment.
 *
 * Only the legacy `#session_id=...` fallback survives. The previous
 * `#google_token=<JWT>` path used `/auth/exchange-token` to convert a raw
 * JWT into a permanent httpOnly cookie, which trivially upgraded any URL
 * fragment leak into a multi-day session. That endpoint now returns 410
 * Gone (see `backend/routes/auth.py`); the in-tree Google login flow
 * writes the cookie directly via `/auth/google/verify-token` /
 * `/auth/google/verify-token-v2` / `/auth/google/session`.
 *
 * If a future backend ever reintroduces a URL-fragment login channel, it
 * MUST mint a single-use, ~60s-TTL ticket and have the frontend POST that
 * ticket here — never the JWT itself.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;

      if (hash.includes('google_token=')) {
        // Legacy URL-fragment JWT path — endpoint is gone, redirect to login
        // so the user retries via the proper Google Sign-In flow that sets
        // the cookie server-side. Never POST raw tokens.
        toast.error('Sign-in link expired. Please try Google sign-in again.');
        window.history.replaceState(null, '', '/teamlogin');
        navigate('/teamlogin', { replace: true });
        return;
      }

      // Legacy flow: session_id (Emergent Auth fallback)
      if (hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        if (!sessionId) {
          navigate('/teamlogin', { replace: true });
          return;
        }
        try {
          const res = await api.post('/auth/google/session', { session_id: sessionId });
          loginWithGoogle(res.data.user);
          window.history.replaceState(null, '', '/app/dashboard');
          toast.success('Welcome back!');
          navigate('/app/dashboard', { replace: true });
        } catch (err) {
          console.error('Google login error:', err);
          toast.error(err.response?.data?.detail || 'Google login failed');
          window.history.replaceState(null, '', '/teamlogin');
          navigate('/teamlogin', { replace: true });
        }
        return;
      }

      // No valid token found
      navigate('/teamlogin', { replace: true });
    };

    processAuth();
  }, [navigate, loginWithGoogle]);

  return (
    <div className="min-h-screen bg-surface-0 flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-sky-500/30 border-t-sky-400 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white text-sm">Completing sign in...</p>
      </div>
    </div>
  );
}
